
import React, { useCallback, useEffect } from 'react';
import styles from './QuoteCard.module.scss';
import { Typography, Tooltip, IconButton, Box, Stack, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import ItemLabel from '../../../components/ItemLabel/ItemLabel';
import { OrderStatusEnum, quoteStatus } from '../../../common/enums';
import { convertDateAndTime, getCommaSeparatedString } from '../../../common/Utils/CommonFunctions/CommonFunctions';
import { OrdersComponentApi, PrescriptionApi } from '../../../constants/ApiConstants';
import { callApi, HTTPMethod } from '../../../services/HttpService/HttpService';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import QuoteTable from '../QuoteTable/QuoteTable';
import Panel from '../../../components/Panel/Panel';
import Icon from '../../../components/Icon/Icon';

const GradientLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 20,
  borderRadius: 10,
  position: 'relative',
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[300],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 10,
    background: theme.palette.primary,
  },
}));


const QuoteCard = ({ data, orderCustomFields, orderStatus, handleOrdersDetails }) => {
  const { t } = useTranslation();
  const userId = useSelector(state => state.authState.userData?.data?.user_info?.user_id);
  const [equipments, setEquipments] = useState(null);
  
  useEffect(() => {
    fetchOrderEquipments();
  },[])
  
  const fetchOrderEquipments = async () => {
    const requestBody = location.pathname.split('/').pop();
    const apiEndpoint = OrdersComponentApi.ordersEquipment.replace("{order_id}", requestBody).replace("{user_id}", userId);
    const response = await callApi(HTTPMethod.Get, apiEndpoint);
    if (response && response.status_code == 200 && response.data) {
      let equipmentList = [...response.data.order_equipment, ...response.data.quote_equipments]
      const equipmentNames = getCommaSeparatedString(equipmentList, "equipment_name");
      setEquipments(equipmentNames);
    }
  };

  const handleDownloadFile = async () => {
    const fileId = data?.pace_quote_file_id;
    const fileResponseView = await callApi(HTTPMethod.Post, PrescriptionApi.viewFile.replace("{file_upload_id}", fileId));
    if (fileResponseView && fileResponseView.status_code == 200) {
      const downloadUrl = fileResponseView.data.url;
      fetch(downloadUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const a = document.createElement('a');
          a.href = url;
          a.download = fileResponseView.data.file_name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        })
        .catch((error) => {
          console.error('Error downloading file:', error);
        });
    }
  };

  const statusUpdatesQuoteOrder = useCallback((orderStatus) => {
  switch (orderStatus) {
    case quoteStatus.providerQuote:
      return { statusValue: 25, statusName: quoteStatus.providerQuote };
    case quoteStatus.stateServeAudit:
      return { statusValue: 50, statusName: quoteStatus.stateServeAudit };
    case quoteStatus.clientReview:
      return { statusValue: 75, statusName: quoteStatus.clientReview };
    default:
      return { statusValue: 100, statusName: quoteStatus.quoteCompleted };
  }
}, []);

const updatedStatus = statusUpdatesQuoteOrder(data?.orders?.order_complete_status);
  
  return (
    <Panel
      title={t("NewOrder.quote")}
      action={
        (orderStatus == OrderStatusEnum.Received || orderStatus == OrderStatusEnum.Canceled) && (
          <Tooltip title={t('Quote.exportQuoteDetailsPDF')}>
            <IconButton color="secondary" size="small" data-testid="downloadPdf" onClick={handleDownloadFile}>
              <Icon icon="fa-download" />
            </IconButton>
          </Tooltip>
        )
      }
      data-testid="QuoteCard"
    >
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="subtitle1" color="text.secondary">
            {t("NewOrder.quoteAndPricing")} <Link href="mailto:PACE@dragonflyhealth.com">{t("NewOrder.paceStateServe")}</Link> {t("NewOrder.directly")}
          </Typography>
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <GradientLinearProgress variant="determinate" value={updatedStatus?.statusValue} className='width100per' />
            <Typography variant="body2" className={`${styles.progressBarStatus} ${updatedStatus?.statusName !== quoteStatus.quoteCompleted ? "colorBlack" : "colorWhite"}`}>
              {updatedStatus?.statusName}
            </Typography>
          </Box>
        </Stack>
        <ItemLabel label="Items" text={equipments} />
        <Box className="textWrap width100per">
          <ItemLabel label="Details" text={data?.orders?.quote_details} />
        </Box>
        <ItemLabel label="Quote Created" text={data?.orders?.quote_created_date_formatted} />
        <ItemLabel label="Provider Quote Submitted" text={data?.orders?.quote_submitted_date_formatted} />
        <ItemLabel label="Dragonfly Health Review Completed" text={data?.orders?.quote_audited_date_formatted} />
        <ItemLabel label="Quote Approval Completed" text={data?.orders?.client_reviewed_date_formatted} />
      </Stack>
      {orderStatus !== quoteStatus.stateServeAudit && (
        <QuoteTable paceQuoteItemList={data?.pace_quote_item_model_list} orderCustomFields={orderCustomFields} orderStatus={orderStatus} handleOrdersDetails={handleOrdersDetails} commentForClient={data?.orders?.comments_for_client} commentsForStateServ={data?.orders?.comments_for_state_serv} commentsForProvider={data?.orders?.comments_for_provider} paceQuoteAccessModel={data?.pace_quote_access_model} />
      )}
    </Panel>
  );
}

export default QuoteCard;
